import type { ExceptionReason, PeriodKind, Weekday } from '@/types/timetable';
import type { LeaveCategory } from '../TeacherTimetableSection';

/**
 * How the week's fixed sets are worded on screen.
 *
 * One place, because the grid, the day page and the availability dialog all
 * print the same labels and three copies would drift the first time a reason is
 * renamed (CLAUDE.md §3).
 */

export const WEEKDAY_LABEL: Record<Weekday, string> = {
  MONDAY: 'Monday',
  TUESDAY: 'Tuesday',
  WEDNESDAY: 'Wednesday',
  THURSDAY: 'Thursday',
  FRIDAY: 'Friday',
  SATURDAY: 'Saturday',
  SUNDAY: 'Sunday',
};

/** Three letters for the grid header, where a full name will not fit. */
export const WEEKDAY_SHORT: Record<Weekday, string> = {
  MONDAY: 'Mon',
  TUESDAY: 'Tue',
  WEDNESDAY: 'Wed',
  THURSDAY: 'Thu',
  FRIDAY: 'Fri',
  SATURDAY: 'Sat',
  SUNDAY: 'Sun',
};

export const REASON_LABEL: Record<ExceptionReason, string> = {
  MEDICAL: 'Medical or emergency',
  CASUAL: 'Casual leave',
  OFFICIAL_DUTY: 'Official duty',
  OTHER: 'Other',
};

/**
 * The stored reason behind each of the availability dialog's categories. Here
 * rather than in the section so the locked-period notice can name the reason
 * without importing the section that renders it.
 */
export const REASON_BY_CATEGORY: Record<LeaveCategory, ExceptionReason> = {
  'Medical / Emergency': 'MEDICAL',
  'Casual Leave': 'CASUAL',
  'Official Duty': 'OFFICIAL_DUTY',
  Other: 'OTHER',
};

/** The options the availability dialog offers, in the order it offers them. */
export const REASON_OPTIONS = (Object.keys(REASON_LABEL) as ExceptionReason[]).map((value) => ({
  value,
  label: REASON_LABEL[value],
}));

/** "08:30 – 09:15" — a period's run, spelled once. */
export function periodRange(startTime: string, endTime: string): string {
  return `${startTime} – ${endTime}`;
}

export const PERIOD_KIND_LABEL: Record<PeriodKind, string> = {
  TEACHING: 'Teaching period',
  BREAK: 'Break',
};

/** What the period form offers, in the order it offers it. */
export const PERIOD_KIND_OPTIONS = (Object.keys(PERIOD_KIND_LABEL) as PeriodKind[]).map(
  (value) => ({ value, label: PERIOD_KIND_LABEL[value] }),
);

/**
 * How one row of the institute day names itself.
 *
 * The institute's own name for it wins wherever it has given one — "Assembly",
 * "Zero period", "Lunch break" — because that is what the staff room calls it.
 * Without one a teaching row is "Period 3" and a break is the plain word,
 * rather than a number nobody says aloud.
 */
export function periodTitle(period: {
  periodNumber: number;
  kind: PeriodKind;
  label: string | null;
}): string {
  if (period.label) return period.label;
  if (period.kind === 'BREAK') return PERIOD_KIND_LABEL.BREAK;

  return `Period ${period.periodNumber}`;
}
